/**
 * Player - Player entity with movement, abilities, health, and stamina
 * Represents the security guard that the player controls
 */

import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import { getInputManager } from '../../engine/InputManager';
import { getPhysicsEngine } from '../../engine/PhysicsEngine';
import { getCollisionSystem } from '../../engine/CollisionSystem';
import { playLandingSound, playJumpSound } from '../audio/SoundEffects';

export class Player extends Entity {
  // Player stats (public for save/load access)
  public stamina: number = 100;
  public maxStamina: number = 100;
  private staminaRegenRate: number = 20; // per second
  private staminaDrainRate: number = 15; // per second when sprinting

  // Progression (public for save/load access)
  public xp: number = 0;
  public level: number = 1;

  // Movement
  private moveSpeed: number = 300; // pixels/second
  private sprintMultiplier: number = 1.5;
  public jumpForce: number = 970; // pixels/second — edit this to change jump height
  private crouchSpeedMultiplier: number = 0.5;

  // Game feel constants
  private readonly coyoteTime: number = 0.12;     // seconds to allow jump after leaving ledge
  private readonly jumpBufferTime: number = 0.12;  // seconds to buffer a jump input before landing
  private readonly jumpCutMultiplier: number = 0.9; // velocity kept when jump is released early

  // Game feel state
  private coyoteTimer: number = 0;
  private jumpBufferTimer: number = 0;
  private wasGrounded: boolean = false;
  private isJumpKeyHeld: boolean = false;

  // Abilities (public for save/load access)
  public flashlightBattery: number = 100;
  private flashlightDrainRate: number = 1; // per second
  private isUsingFlashlight: boolean = false;
  private isUsingMask: boolean = false;
  private isHiding: boolean = false;
  private isCrouching: boolean = false;
  private isSprinting: boolean = false;

  // Animation state
  private currentAnimation: string = 'idle';
  private facingDirection: 'left' | 'right' = 'right';

  constructor(id: string, position: Vector2, size: { width: number; height: number }) {
    super(id, 'player', position, size);

    // Set player-specific health
    this.maxHealth = 100;
    this.health = this.maxHealth;

    // Log jump force for debugging
    console.log(`Player created with jump force: ${this.jumpForce}`);
  }

  /**
   * Register player with physics engine
   */
  protected override registerPhysics(): void {
    const physicsEngine = getPhysicsEngine();
    physicsEngine.addEntity(this as any, {
      mass: 1.0,
      gravity: 1.0, // Full gravity
      friction: 0.8,
      restitution: 0.0,
      isKinematic: false,
      isGrounded: false,
      canJump: true,
    });
  }

  /**
   * Register player with collision system
   */
  protected override registerCollision(): void {
    const collisionSystem = getCollisionSystem();
    collisionSystem.addCollider(this as any, {
      type: 'box',
      isTrigger: false,
      layer: 'player',
    });
  }

  /**
   * Update player logic
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    const physicsEngine = getPhysicsEngine();
    const isGrounded = physicsEngine.isGrounded(this.id);

    // ── Coyote time ────────────────────────────────────────────────────
    // Give a brief jump window after walking off a ledge
    if (this.wasGrounded && !isGrounded && this.velocity.y <= 0) {
      this.coyoteTimer = this.coyoteTime;
    }
    if (this.coyoteTimer > 0) {
      this.coyoteTimer -= deltaTime;
      if (!isGrounded) physicsEngine.setCanJump(this.id, true);
    }
    this.wasGrounded = isGrounded;

    // ── Variable jump height — cut upward velocity on early release ────
    if (!this.isJumpKeyHeld && this.velocity.y > 0) {
      this.velocity.y *= Math.pow(this.jumpCutMultiplier, deltaTime * 60); // frame-rate independent
    }

    // ── Jump buffer — execute buffered jump on landing ─────────────────
    if (this.jumpBufferTimer > 0) {
      this.jumpBufferTimer -= deltaTime;
      if (isGrounded || physicsEngine.canJump(this.id)) {
        this.executeJump();
      }
    }

    // Handle input
    this.handleInput(deltaTime);

    // Update stamina
    this.updateStamina(deltaTime);

    // Update flashlight battery
    if (this.isUsingFlashlight) {
      this.drainFlashlight(deltaTime);
    }

    // Update animation state
    this.updateAnimation();
  }

  /**
   * Render player (override in subclass with visual representation)
   */
  public render(): void {
    // To be implemented with Three.js sprite rendering
  }

  /**
   * Handle player input
   */
  private handleInput(_deltaTime: number): void {
    const inputManager = getInputManager();
    const physicsEngine = getPhysicsEngine();

    // Don't allow movement if hiding
    if (this.isHiding) {
      return;
    }

    // Crouch toggle
    if (inputManager.isKeyPressed('KeyC') || inputManager.isKeyPressed('ControlLeft') || inputManager.isKeyPressed('ControlRight')) {
      this.isCrouching = !this.isCrouching;
    }

    // Mask toggle
    if (inputManager.isKeyPressed('KeyM')) {
      this.toggleMask();
    }

    // Flashlight toggle
    if (inputManager.isKeyPressed('KeyF')) {
      this.toggleFlashlight();
    }

    // Sprint (hold Shift)
    const wantsToSprint = inputManager.isKeyDown('ShiftLeft') || inputManager.isKeyDown('ShiftRight');
    this.isSprinting = wantsToSprint && this.stamina > 0 && !this.isCrouching;

    // Horizontal movement
    let moveDirection = 0;
    const aDown = inputManager.isKeyDown('KeyA');
    const dDown = inputManager.isKeyDown('KeyD');
    const leftDown = inputManager.isKeyDown('ArrowLeft');
    const rightDown = inputManager.isKeyDown('ArrowRight');

    if (aDown || leftDown) {
      moveDirection = -1;
      this.facingDirection = 'left';
    } else if (dDown || rightDown) {
      moveDirection = 1;
      this.facingDirection = 'right';
    }

    // Calculate speed with modifiers
    let currentSpeed = this.moveSpeed;
    if (this.isCrouching) {
      currentSpeed *= this.crouchSpeedMultiplier;
    } else if (this.isSprinting) {
      currentSpeed *= this.sprintMultiplier;
    }

    // Apply horizontal movement — only set velocity when actively moving;
    // releasing keys lets physics friction decelerate naturally (no jarring instant stop)
    if (moveDirection !== 0) {
      physicsEngine.moveHorizontal(this.id, moveDirection, currentSpeed);
    }

    // ── Jump input ─────────────────────────────────────────────────────
    const jumpKeyDown = inputManager.isKeyDown('Space') || inputManager.isKeyDown('KeyW') || inputManager.isKeyDown('ArrowUp');
    const jumpJustPressed = inputManager.isKeyPressed('Space') || inputManager.isKeyPressed('KeyW') || inputManager.isKeyPressed('ArrowUp');
    this.isJumpKeyHeld = jumpKeyDown;

    if (jumpJustPressed) {
      const canJumpNow = physicsEngine.canJump(this.id) || this.coyoteTimer > 0;
      if (canJumpNow) {
        this.executeJump();
      } else {
        // Buffer the jump — execute it the moment we land
        this.jumpBufferTimer = this.jumpBufferTime;
      }
    }
  }

  /**
   * Execute a jump — ensures canJump is set (coyote time) and resets timers
   */
  private executeJump(): boolean {
    const physicsEngine = getPhysicsEngine();

    // Allow jump during coyote window even if not grounded
    if (this.coyoteTimer > 0) {
      physicsEngine.setCanJump(this.id, true);
    }

    const success = physicsEngine.jump(this.id, this.jumpForce);
    if (success) {
      playJumpSound();
      this.coyoteTimer = 0;      // Consume coyote window
      this.jumpBufferTimer = 0;  // Consume buffer
    }
    return success;
  }

  /**
   * Update stamina
   */
  private updateStamina(deltaTime: number): void {
    if (this.isSprinting) {
      // Drain stamina when sprinting
      this.stamina = Math.max(0, this.stamina - this.staminaDrainRate * deltaTime);

      // Stop sprinting if stamina depleted
      if (this.stamina <= 0) {
        this.isSprinting = false;
      }
    } else {
      // Regenerate stamina when not sprinting
      this.stamina = Math.min(this.maxStamina, this.stamina + this.staminaRegenRate * deltaTime);
    }
  }

  /**
   * Drain flashlight battery
   */
  private drainFlashlight(deltaTime: number): void {
    this.flashlightBattery = Math.max(0, this.flashlightBattery - this.flashlightDrainRate * deltaTime);

    // Turn off flashlight if battery depleted
    if (this.flashlightBattery <= 0) {
      this.isUsingFlashlight = false;
    }
  }

  /**
   * Toggle flashlight
   */
  public toggleFlashlight(): void {
    if (this.flashlightBattery > 0) {
      this.isUsingFlashlight = !this.isUsingFlashlight;
    }
  }

  /**
   * Toggle mask
   */
  public toggleMask(): void {
    this.isUsingMask = !this.isUsingMask;
  }

  /**
   * Enter hiding spot
   */
  public hide(): void {
    this.isHiding = true;
    this.velocity.x = 0;
    this.velocity.y = 0;
  }

  /**
   * Exit hiding spot
   */
  public unhide(): void {
    this.isHiding = false;
  }

  /**
   * Update animation state based on current state
   */
  private updateAnimation(): void {
    const physicsEngine = getPhysicsEngine();
    const isGrounded = physicsEngine.isGrounded(this.id);
    const isMoving = Math.abs(this.velocity.x) > 1;

    if (this.isHiding) {
      this.currentAnimation = 'hiding';
    } else if (!isGrounded) {
      this.currentAnimation = 'jump';
    } else if (this.isCrouching) {
      this.currentAnimation = isMoving ? 'crouch_walk' : 'crouch';
    } else if (isMoving) {
      this.currentAnimation = this.isSprinting ? 'run' : 'walk';
    } else {
      this.currentAnimation = 'idle';
    }
  }

  /**
   * Refill flashlight battery
   */
  public refillFlashlight(amount: number): void {
    this.flashlightBattery = Math.min(100, this.flashlightBattery + amount);
  }

  /**
   * Upgrade player stat
   */
  public upgradeStat(stat: 'speed' | 'health' | 'stamina', amount: number): void {
    switch (stat) {
      case 'speed':
        this.moveSpeed += amount;
        break;
      case 'health':
        this.maxHealth = Math.min(500, this.maxHealth + amount);
        this.health = this.maxHealth; // Full heal on upgrade
        break;
      case 'stamina':
        this.maxStamina = Math.min(200, this.maxStamina + amount);
        this.stamina = this.maxStamina; // Full stamina on upgrade
        break;
    }
  }

  /**
   * Called when player lands on the ground
   */
  protected override onLanding(landingVelocity: number): void {
    // Play landing sound effect based on fall velocity
    playLandingSound(landingVelocity);
  }

  /**
   * Override onDeath to allow respawning instead of destroying
   */
  protected override onDeath(): void {
    console.log('Player: Player died');
    // Don't call destroy() - let the Level handle respawning
    // The Level will detect the player is dead and trigger respawn
  }

  // Getters
  public getStamina(): number {
    return this.stamina;
  }

  public getMaxStamina(): number {
    return this.maxStamina;
  }

  public getFlashlightBattery(): number {
    return this.flashlightBattery;
  }

  public isFlashlightOn(): boolean {
    return this.isUsingFlashlight;
  }

  public isMaskOn(): boolean {
    return this.isUsingMask;
  }

  public isPlayerHiding(): boolean {
    return this.isHiding;
  }

  public isPlayerCrouching(): boolean {
    return this.isCrouching;
  }

  public isPlayerSprinting(): boolean {
    return this.isSprinting;
  }

  public getCurrentAnimation(): string {
    return this.currentAnimation;
  }

  public getFacingDirection(): 'left' | 'right' {
    return this.facingDirection;
  }

  public getMoveSpeed(): number {
    return this.moveSpeed;
  }
}
